import { LoadingOutlined, UploadOutlined } from '@ant-design/icons';
import { Upload, Spin } from 'antd';
import * as React from 'react';
import LogoImage from './LogoImage';
import { useTranslation } from 'react-i18next';
import DesignModel from '../models/DesignModel';
import { observer } from 'mobx-react-lite';
import ImageWebsiteLogo from './files/ImageWebsiteLogo';
import AntdCustomButton from '../../../components/buttons/AntdCustomButton';
import { SmallText } from '../../../components/ui/Typography';
import useLogoUpload from '../../../hooks/useLogoUpload';

interface FCProps {
  selectedLogo: ImageWebsiteLogo | null;
  onChange: (file: ImageWebsiteLogo | null) => void;
}

const LogoUploader: React.FC<FCProps> = observer(
  ({ selectedLogo, onChange }) => {
    const [continueWithoutLogo, setContinueWithoutLogo] = React.useState(false);
    const [isHovered, setIsHovered] = React.useState(false);

    const { t } = useTranslation();

    const { isUploading, uploadProps } = useLogoUpload({
      onSuccess: (logo) => {
        onChange(logo);
      },
      onError: (error) => {
        console.error('Upload failed:', error);
      }
    });

    const handleLogoSelect = (logo: ImageWebsiteLogo) => {
      setContinueWithoutLogo(false);
      onChange(logo);
    };

    const handleContinueWithoutLogo = (checked: boolean) => {
      setContinueWithoutLogo(checked);
      if (checked) {
        onChange(null);
      }
    };

    let logos = [
      ...(DesignModel.allFoundLogos || []),
      ...(DesignModel.uploadedLogos || [])
    ];

    return (
      <div>
        <div
          className={`flex flex-col md:flex-row ${
            logos.length > 5 ? 'flex-wrap' : ''
          } sm:align-center sm:justify-center mb-[48px]`}
        >
          {logos.map((logo, index) => (
            <LogoImage
              onSelect={handleLogoSelect}
              selected={
                selectedLogo ? selectedLogo.getId() === logo.getId() : false
              }
              key={logo.getId()}
              logo={logo}
            />
          ))}
        </div>

        <div className="mb-[12px] flex flex-col sm:flex-row gap-4 sm:items-center justify-center">
          <Upload {...uploadProps}>
            <AntdCustomButton
              appearance="default"
              style={{ backgroundColor: 'transparent', padding: '5px 16px' }}
              icon={
                isUploading ? (
                  <Spin
                    indicator={
                      <LoadingOutlined style={{ fontSize: 18 }} spin />
                    }
                  />
                ) : (
                  <UploadOutlined />
                )
              }
              disabled={isUploading}
            >
              {isUploading ? t('Uploading...') : t('Upload the file')}
            </AntdCustomButton>
          </Upload>
          
          <span
            className="flex items-center gap-2 border max-w-[200px]"
            style={{
              border: `1px solid ${isHovered ? '#5db5e8' : '#d9d9d9'}`,
              cursor: 'pointer',
              padding: '8px 16px',
              borderRadius: '50px',
              transition: 'border-color 0.2s'
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                className="cursor-pointer"
                type="checkbox"
                checked={continueWithoutLogo}
                onChange={(e) => handleContinueWithoutLogo(e.target.checked)}
              />
              {t('Continue without a logo')}
            </label>
          </span>
        </div>
        
        <SmallText>
          {t('The following file formats are supported: JPG, PNG, WebP, Avif, SVG, GIF')}
        </SmallText>
      </div>
    );
  }
);

export default LogoUploader;